import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, MapPin, Star, ShieldCheck } from "lucide-react";
import { STATS, CONTACT, IMAGES } from "@/lib/data";
import { useI18n } from "@/i18n";

const lineVariant = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.7, delay: 0.05 + i * 0.06, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const { t, localePath } = useI18n();
  const heroLines = t("hero.lines");

  return (
    <section id="top" className="relative bg-[#0E3B3B] text-white pt-32 sm:pt-36 lg:pt-44 pb-16 overflow-hidden">
      {/* Decorative layers */}
      <div className="absolute inset-0 opacity-[0.18] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 82% 12%, rgba(16,185,129,0.6), transparent 42%)" }} />
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)", backgroundSize: "26px 26px" }} />

      <div className="relative z-[2] max-w-[1400px] mx-auto w-full px-5 sm:px-8">
        <div className="grid lg:grid-cols-[1.08fr_0.92fr] gap-12 lg:gap-14 items-center">
          {/* Left copy */}
          <div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full border border-white/15 bg-white/[0.06] mb-7">
              <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" />
              <span className="font-mono-tech text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-semibold">
                {t("hero.badge")}
              </span>
            </motion.div>

            <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.98] text-3xl sm:text-4xl lg:text-5xl">
              {heroLines.map((line, i) => (
                <span key={i} className="reveal-mask">
                  <motion.span custom={i} variants={lineVariant} initial="hidden" animate="visible" className="block">
                    {i === heroLines.length - 1 ? <span className="text-emerald-400">{line}</span> : line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }}
              className="mt-7 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed">
              {t("hero.subtitle_a")} <span className="text-white font-semibold">{t("hero.subtitle_b")}</span> {t("hero.subtitle_c")}
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22, duration: 0.6 }}
              className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link to={localePath("/quick-quote")} className="group inline-flex items-center gap-3 pl-7 pr-5 py-4 rounded-full bg-emerald-500 text-[#0A2C2C] font-bold hover:bg-emerald-400 active:scale-95 transition-all shadow-lg shadow-emerald-500/20" data-testid="hero-quote-btn">
                {t("cta.requestQuote")}
                <span className="grid place-items-center w-6 h-6 rounded-full bg-[#0A2C2C] text-emerald-400 group-hover:translate-x-0.5 transition-transform"><ArrowRight size={14} strokeWidth={3} /></span>
              </Link>
              <a href={CONTACT.phoneHref} className="inline-flex items-center gap-3 px-7 py-4 rounded-full border border-white/25 text-white font-semibold hover:border-emerald-400 hover:text-emerald-300 transition-all" data-testid="phone-cta-hero">
                <Phone size={18} /> {CONTACT.phone}
              </a>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.6 }}
              className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex items-center gap-2">
                <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={15} className="fill-amber-400 text-amber-400" />)}</div>
                <span className="text-sm text-white/75"><span className="font-semibold text-white">5.0</span> {t("hero.ratedGoogle")}</span>
              </div>
              <span className="hidden sm:block w-px h-4 bg-white/20" />
              <span className="flex items-center gap-1.5 text-sm text-white/75"><ShieldCheck size={15} className="text-emerald-400" /> {t("hero.bbb")}</span>
            </motion.div>
          </div>

          {/* Right product panel */}
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative">
            <div className="relative rounded-[2rem] bg-[#FBFAF8] p-6 sm:p-8 shadow-2xl">
              <motion.img src={IMAGES.frontloadDumpster} alt="EWS front-load commercial dumpster"
                fetchPriority="high" decoding="async" width="1024" height="1024"
                className="w-full rounded-2xl" />
              {/* floating chips */}
              <div className="absolute top-4 left-4 hazard-stripe h-2 w-16 rounded-full" />
              <span className="absolute -top-4 right-6 px-4 py-2 rounded-full bg-[#0E3B3B] text-white font-mono-tech text-[10px] uppercase tracking-widest border border-emerald-400/30 shadow-lg">
                {t("hero.dumpsterChip")}
              </span>
              <span className="absolute -bottom-4 left-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500 text-[#0A2C2C] font-bold text-sm shadow-lg">
                <span className="w-2 h-2 rounded-full bg-[#0A2C2C]" /> {t("hero.saveChip")}
              </span>
            </div>
            {/* floating stat card */}
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }}
              className="absolute -right-3 sm:-right-6 top-1/2 -translate-y-1/2 hidden sm:block bg-white text-[#0B0F14] rounded-2xl px-5 py-4 shadow-2xl border border-black/5">
              <div className="font-display font-extrabold text-3xl leading-none">30<span className="text-emerald-600">+</span></div>
              <div className="text-[11px] text-slate-500 mt-1 max-w-[80px]">{t("hero.yearsExp")}</div>
            </motion.div>
          </motion.div>
        </div>

        {/* Glass stats band */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32, duration: 0.6 }}
          className="mt-16 lg:mt-24 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STATS.map((s, i) => (
            <div key={s.label} className="rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-sm p-5 sm:p-6 hover:bg-white/[0.1] transition-colors">
              <div className="font-display font-extrabold text-3xl sm:text-4xl text-white">{s.metric}</div>
              <div className="mt-1.5 flex items-start gap-1.5 text-xs sm:text-sm text-white/65">
                <MapPin size={13} className="text-emerald-400 mt-0.5 shrink-0" /> {t(`stat.${i}`)}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
